import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Building, Plus, Calendar, User, LogOut, Settings, Menu, X, UserCircle, Bell } from 'lucide-react';
import api from '../api';

export default function PartnerNavbar() {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showMobileMenu, setShowMobileMenu] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [showNotifDrop, setShowNotifDrop] = useState(false);
    const notifRef = useRef(null);

    useEffect(() => {
        api.getPartnerNotifications().then(setNotifications).catch(() => {});
    }, []);

    useEffect(() => {
        const h = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifDrop(false); };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, []);

    const handleMarkAllRead = async () => {
        try { await api.markAllPartnerNotificationsRead(); setNotifications(prev => prev.map(n => ({ ...n, read: true }))); } catch {}
    };

    const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
    const unreadCount = notifications.filter(n => !n.read).length;

    return (
        <header className="header">
            <div className="container header-content">
                <Link to="/partner" className="logo">
                    <img src="/atam-logo.svg" alt="ATAM" style={{ height: '32px', width: 'auto' }} />
                </Link>

                {/* Desktop Nav — Dashboard | Submit Event | Browse Events */}
                <nav className="nav">
                    <Link to="/partner" className={`nav-link ${isActive('/partner') && location.pathname === '/partner' ? 'active' : ''}`}>
                        <Building size={15} style={{ marginRight: 5 }} />
                        Dashboard
                    </Link>
                    <Link to="/partner/submit" className={`nav-link ${location.pathname.startsWith('/partner/submit') ? 'active' : ''}`}>
                        <Plus size={15} style={{ marginRight: 5 }} />
                        Submit Event
                    </Link>
                    <Link to="/events" className={`nav-link ${location.pathname === '/events' ? 'active' : ''}`}>
                        <Calendar size={15} style={{ marginRight: 5 }} />
                        Browse Events
                    </Link>
                </nav>

                {/* User dropdown + Notification Bell */}
                <div className="auth-buttons" style={{ gap: 8 }}>
                    {/* Bell */}
                    <div ref={notifRef} style={{ position: 'relative' }}>
                        <button
                            style={{
                                background: unreadCount > 0 ? 'rgba(99,102,241,0.08)' : 'transparent',
                                border: `1px solid ${unreadCount > 0 ? 'rgba(99,102,241,0.25)' : 'var(--border)'}`,
                                borderRadius: 10, padding: '7px 10px', cursor: 'pointer',
                                color: unreadCount > 0 ? '#6366f1' : 'var(--text-secondary)',
                                display: 'flex', alignItems: 'center', gap: 4, position: 'relative'
                            }}
                            onClick={() => setShowNotifDrop(v => !v)}
                        >
                            <Bell size={17} />
                            {unreadCount > 0 && (
                                <span style={{
                                    background: '#ef4444', color: '#fff', fontSize: '0.65rem', fontWeight: 700,
                                    borderRadius: '50%', width: 16, height: 16, display: 'flex',
                                    alignItems: 'center', justifyContent: 'center',
                                    position: 'absolute', top: -5, right: -5
                                }}>{unreadCount > 9 ? '9+' : unreadCount}</span>
                            )}
                        </button>
                        {showNotifDrop && (
                            <div style={{
                                position: 'absolute', top: '110%', right: 0, zIndex: 200,
                                background: '#fff', borderRadius: 14, width: 300,
                                boxShadow: '0 8px 24px rgba(0,0,0,0.12)', border: '1px solid var(--border)', overflow: 'hidden'
                            }}>
                                <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>Notifications</span>
                                    {unreadCount > 0 && <button onClick={handleMarkAllRead} style={{ fontSize: '0.75rem', color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer' }}>Mark all read</button>}
                                </div>
                                {notifications.length === 0 ? (
                                    <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.875rem' }}>All caught up! 🎉</div>
                                ) : notifications.slice(0, 6).map(n => (
                                    <div key={n.id} style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', background: n.read ? 'transparent' : 'rgba(99,102,241,0.04)' }}>
                                        <div style={{ fontWeight: 600, fontSize: '0.8rem', marginBottom: 2 }}>{n.title}</div>
                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{n.message}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* User menu */}
                    {user && (
                        <div style={{ position: 'relative' }}>
                            <button
                                className="btn btn-secondary"
                                onClick={() => setShowUserMenu(!showUserMenu)}
                                style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                            >
                                <User size={18} />
                                {user.name}
                            </button>

                            {showUserMenu && (
                                <div
                                    style={{
                                        position: 'absolute', top: '110%', right: 0, marginTop: 4,
                                        background: 'var(--bg-card)', border: '1px solid var(--border)',
                                        borderRadius: 14, padding: 8, minWidth: 200, zIndex: 100,
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.1)'
                                    }}
                                    // Close on outside click
                                    onMouseLeave={() => setShowUserMenu(false)}
                                >
                                    <div style={{ padding: '8px 12px 12px', borderBottom: '1px solid var(--border)', marginBottom: 6 }}>
                                        <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-primary)' }}>{user.name}</div>
                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>{user.email}</div>
                                    </div>

                                    <Link
                                        to="/partner/profile"
                                        className="btn btn-ghost"
                                        style={{ width: '100%', justifyContent: 'flex-start', gap: 8 }}
                                        onClick={() => setShowUserMenu(false)}
                                    >
                                        <UserCircle size={16} /> My Profile
                                    </Link>
                                    <Link
                                        to="/change-password"
                                        className="btn btn-ghost"
                                        style={{ width: '100%', justifyContent: 'flex-start', gap: 8 }}
                                        onClick={() => setShowUserMenu(false)}
                                    >
                                        <Settings size={16} /> Change Password
                                    </Link>
                                    <div style={{ borderTop: '1px solid var(--border)', marginTop: 6, paddingTop: 6 }}>
                                        <button
                                            className="btn btn-ghost"
                                            style={{ width: '100%', justifyContent: 'flex-start', gap: 8, color: 'var(--error)' }}
                                            onClick={() => { logout(); setShowUserMenu(false); }}
                                        >
                                            <LogOut size={16} /> Logout
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Mobile hamburger */}
                <button
                    className="mobile-menu-btn"
                    onClick={() => setShowMobileMenu(!showMobileMenu)}
                    aria-label="Toggle Menu"
                >
                    {showMobileMenu ? <X size={20} /> : <Menu size={20} />}
                </button>
            </div>

            {showMobileMenu && (
                <div className="mobile-menu">
                    <Link to="/partner" className={`mobile-nav-link ${location.pathname === '/partner' ? 'active' : ''}`} onClick={() => setShowMobileMenu(false)}>
                        <Building size={16} style={{ marginRight: 8 }} /> Dashboard
                    </Link>
                    <Link to="/partner/submit" className={`mobile-nav-link ${location.pathname.startsWith('/partner/submit') ? 'active' : ''}`} onClick={() => setShowMobileMenu(false)}>
                        <Plus size={16} style={{ marginRight: 8 }} /> Submit Event
                    </Link>
                    <Link to="/events" className={`mobile-nav-link ${location.pathname === '/events' ? 'active' : ''}`} onClick={() => setShowMobileMenu(false)}>
                        <Calendar size={16} style={{ marginRight: 8 }} /> Browse Events
                    </Link>
                    <div className="mobile-menu-divider" />
                    <Link to="/partner/profile" className="mobile-nav-link" onClick={() => setShowMobileMenu(false)}>
                        <UserCircle size={16} style={{ marginRight: 8 }} /> My Profile
                    </Link>
                    <Link to="/change-password" className="mobile-nav-link" onClick={() => setShowMobileMenu(false)}>
                        <Settings size={16} style={{ marginRight: 8 }} /> Change Password
                    </Link>
                    <button
                        className="mobile-nav-link"
                        style={{ color: 'var(--error)', width: '100%', justifyContent: 'flex-start' }}
                        onClick={() => { logout(); setShowMobileMenu(false); }}
                    >
                        <LogOut size={16} style={{ marginRight: 8 }} /> Logout
                    </button>
                </div>
            )}
        </header>
    );
}